import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import { ProcessedCourse } from "@/common/course/types";
import { ReactElement, useEffect, useState } from "react";
import { CourseRun } from "@/common/courseRun/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/Dialog";
import { getCourseRunStartText } from "@/lib/course/utils/getCourseRunTilePrimaryText";
import { getCourseRunEndText } from "@/lib/course/utils/getCourseRunTitleSecondaryText";
import { sortCourseRuns } from "@/components/CourseContent/ContentRefresh/CourseRunSelector/utils/sortCourseRuns";
import { selectOptimalCourseRun } from "./utils/selectOptimalCourseRun";
import useTranslations from "@/i18n/useTranslations";

const MultipleStartDateModal = ({
  course,
  showEnrollmentCount = false,
  children,
}: {
  course: ProcessedCourse;
  showEnrollmentCount: boolean | 0 | null;
  children: ReactElement;
}) => {
  const t = useTranslations("Course");
  const translate =
    t as unknown as import("@/lib/course/utils/getCourseInformation").CourseInfoTranslate;
  const [open, setOpen] = useState(false);
  const [courseRun, setCourseRun] = useState<CourseRun | undefined>(undefined);
  const sortedCourseRuns = sortCourseRuns(course.activeCourseRuns);

  useEffect(() => {
    const optimalCourseRun = selectOptimalCourseRun(course.activeCourseRuns);
    setCourseRun(optimalCourseRun);
  }, [course.activeCourseRuns]);

  const setValue = (value: string) => {
    if (course.activeCourseRuns) {
      const run = course.activeCourseRuns.find(
        (courseRun) => courseRun.id === value,
      );
      setCourseRun(run);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="p-0 gap-0 bg-putty-200">
        <DialogHeader className="p-8">
          <DialogTitle className="text-4xl font-inter italic !font-black">
            Choose your start date
          </DialogTitle>
        </DialogHeader>
        <div>
          {sortedCourseRuns ? (
            <div className="food-radio-inputs px-8 flex gap-5 flex-col md:flex-row md:flex-wrap">
              {sortedCourseRuns.map((option) => (
                <label
                  key={option.id}
                  htmlFor={option.id}
                  className="flex justify-center items-center py-4 px-8 border-2 rounded-xl gap-3 bg-putty-100 cursor-pointer"
                  onClick={() => setValue(option.id)}
                >
                  <input
                    type="radio"
                    id={option.id}
                    name="courseRun"
                    value={option.id}
                    checked={courseRun?.id === option.id}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-5 h-5 border-2 border-[#424242]"
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold text-md">
                      {`${getCourseRunStartText(option, true, translate)}`}
                    </span>
                    <span className="text-sm">
                      {`${getCourseRunEndText(option, translate)}`}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          ) : null}
        </div>
        <DialogFooter className="flex flex-col-reverse justify-center items-center  md:flex-row  h-32 md:h-24 p-8">
          {showEnrollmentCount ? (
            <div className="flex items-center text-sm mt-6 md:mt-0">
              <span className="font-bold">
                {course.enrollmentCount.toLocaleString()}
              </span>
              <span className="m-0">&nbsp;{t("learnersEnrolled")}</span>
            </div>
          ) : null}
          {courseRun !== undefined ? (
            <EnrollLink
              courseRun={courseRun}
              course={course}
              onClick={() => setOpen(false)}
            />
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MultipleStartDateModal;
